import React, { useMemo } from 'react';
import Streamline from '@/components/shared/streamline/Streamline';
import { cdnImage } from '@/utils/image-utils';
import { CDN_IMAGES_PROMOTIONS_PATH } from '@/constants/constants';
import { Button } from '@/components/buttons/Button';
import { GTagEvents, triggerGTag } from '@/lib/gTagManager';
import { useAbsoluteNavigate } from '@/hooks/useAbsoluteNavigate';
import { PromotionWidgetVm } from '@/models/vm/promotion-widget-vm.model';
import { useIntl } from 'react-intl';
import classNames from 'classnames';

const MAX_VISIBLE_PROMOS = 7;

function PromotionsStreamline({ promotions }: { promotions: PromotionWidgetVm[] }) {
  const navigate = useAbsoluteNavigate();
  const intl = useIntl();

  const onMoreInfoHandler = (promotion: PromotionWidgetVm) => {
    triggerGTag(GTagEvents.promotion_streamline_cta, {
      slug: promotion.slug,
    });
    navigate(`/promotion/${promotion.slug}`);
  };

  const onViewAllPromotions = () => {
    triggerGTag(GTagEvents.promotion_streamline_view_all);
    navigate(`/promotions`);
  };

  const children = useMemo(() => {
    const shownPromotions = promotions.slice(0, Math.min(MAX_VISIBLE_PROMOS, promotions.length));
    const promotionCards = shownPromotions.map(promotion => (
      <div
        key={`promotion-${promotion.slug}`}
        onClick={() => onMoreInfoHandler(promotion)}
        className={classNames(
          'w-[350px] relative shrink-0 rounded-3xl overflow-hidden cursor-pointer min-h-[130px]',
          'transition-all active:scale-[98%] ease-in-out duration-300 hover:border-blue-blueMedium border-[2px] border-transparent',
        )}
      >
        {promotion.image && (
          <div className="absolute inset-0 flex w-full justify-end">
            <img
              src={cdnImage(promotion.image, {
                width: '350',
                height: 'auto',
                path: CDN_IMAGES_PROMOTIONS_PATH,
                dpr: '2',
              })}
              alt={promotion.widgetTitle}
              className="object-cover object-left-top h-full w-full"
            />
          </div>
        )}
        <div className="absolute top-0 left-0 right-0 bottom-0" />
      </div>
    ));

    // if there are more promotions, add a VIEW ALL promotion card
    if (promotions.length > MAX_VISIBLE_PROMOS) {
      promotionCards.push(
        <div
          key={`promotion-view-all`}
          className="w-[320px] sm-w-[426px] h-[128px] relative shrink-0 rounded-md overflow-hidden"
        >
          <div className="absolute top-0 left-0 right-0 bottom-0">
            <img
              src={cdnImage('promotions_view_all_card.jpg', {
                width: '320',
                height: '128',
                fit: 'cover',
                dpr: '2',
              })}
              alt={intl.formatMessage({ id: 'promotions.all' })}
              className="object-cover"
            />
          </div>
          <div className="absolute top-0 left-0 right-0 bottom-0 bg-gradient-to-r from-black from-10%"></div>
          <div className="relative text-white p-4 flex flex-col justify-between h-full">
            <div>
              <div className="font-bold truncate">
                {intl.formatMessage({ id: 'promotions.all' })}
              </div>
            </div>
            <Button
              onClickHandler={onViewAllPromotions}
              className="!w-fit px-8"
              size="xs"
              text={intl.formatMessage({ id: 'games.showAll' })}
            />
          </div>
        </div>,
      );
    }

    return promotionCards;
  }, [promotions]);

  return <Streamline children={children} />;
}

export default PromotionsStreamline;
